import React, { useState, useEffect, useCallback } from 'react';
import { axiosInstance } from '../App';
import { toast } from 'sonner';
import {
  FileText, Send, Banknote, RotateCcw, Archive, Loader2,
  X, CheckCircle2, Circle, ExternalLink, AlertTriangle,
} from 'lucide-react';

/** Visual config per event type. */
const EVENT_VISUAL = {
  created:            { icon: FileText,   color: 'slate',   ring: 'ring-slate-300' },
  sent:               { icon: Send,       color: 'sky',     ring: 'ring-sky-300' },
  payment_posted:     { icon: Banknote,   color: 'emerald', ring: 'ring-emerald-300' },
  credit_note_posted: { icon: RotateCcw,  color: 'amber',   ring: 'ring-amber-300' },
  archived:           { icon: Archive,    color: 'rose',    ring: 'ring-rose-300' },
  event:              { icon: Circle,     color: 'slate',   ring: 'ring-slate-300' },
};

const STATUS_BADGES = {
  draft:           'bg-slate-100 text-slate-700 border-slate-300',
  sent:            'bg-sky-100 text-sky-800 border-sky-300',
  paid:            'bg-emerald-100 text-emerald-800 border-emerald-300',
  partially_paid:  'bg-amber-100 text-amber-800 border-amber-300',
  cancelled:       'bg-rose-100 text-rose-800 border-rose-300',
};

const formatMoney = (n) => Number(n || 0).toLocaleString('en-US', {
  minimumFractionDigits: 2, maximumFractionDigits: 2,
});

const formatDateTime = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' }); }
  catch { return iso; }
};

const COLOR_CLASSES = {
  slate:   { bg: 'bg-slate-500',   ring: 'ring-slate-200',   text: 'text-slate-700',   light: 'bg-slate-50',   border: 'border-slate-200' },
  sky:     { bg: 'bg-sky-500',     ring: 'ring-sky-200',     text: 'text-sky-700',     light: 'bg-sky-50',     border: 'border-sky-200' },
  emerald: { bg: 'bg-emerald-500', ring: 'ring-emerald-200', text: 'text-emerald-700', light: 'bg-emerald-50', border: 'border-emerald-200' },
  amber:   { bg: 'bg-amber-500',   ring: 'ring-amber-200',   text: 'text-amber-700',   light: 'bg-amber-50',   border: 'border-amber-200' },
  rose:    { bg: 'bg-rose-500',    ring: 'ring-rose-200',    text: 'text-rose-700',    light: 'bg-rose-50',    border: 'border-rose-200' },
};

export default function InvoiceLifecycleWizard({ invoiceId, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      const { data } = await axiosInstance.get(`/invoices/${invoiceId}/timeline`);
      setData(data);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
      toast.error('فشل تحميل الـ Timeline');
    } finally { setLoading(false); }
  }, [invoiceId]);

  useEffect(() => { if (invoiceId) load(); }, [invoiceId, load]);

  if (!invoiceId) return null;

  return (
    <div
      className="fixed inset-0 z-[9000] flex items-center justify-center bg-slate-900/70 backdrop-blur-sm p-4"
      data-testid="invoice-lifecycle-modal"
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
    >
      <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-slate-900 to-slate-700 text-white px-6 py-4 flex items-start justify-between">
          <div>
            <h2 className="text-lg font-bold flex items-center gap-2">
              <FileText className="w-5 h-5" />
              دورة حياة الفاتورة · Invoice Lifecycle
            </h2>
            {data?.invoice_number && (
              <div className="text-sm text-slate-300 mt-1 flex items-center gap-2">
                <span className="font-mono">#{data.invoice_number}</span>
                {data.current_status && (
                  <span className={`px-2 py-0.5 rounded-md text-xs font-medium border ${STATUS_BADGES[data.current_status] || 'bg-slate-200 text-slate-700'}`}>
                    {data.current_status}
                  </span>
                )}
                {data.is_archived && (
                  <span className="px-2 py-0.5 rounded-md text-xs font-medium bg-rose-100 text-rose-800 border border-rose-300">
                    مؤرشفة
                  </span>
                )}
              </div>
            )}
          </div>
          <button
            onClick={() => onClose?.()}
            className="p-1.5 hover:bg-white/10 rounded text-white/80 hover:text-white"
            data-testid="lifecycle-close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Summary band */}
        {data?.summary && (
          <div className="grid grid-cols-4 gap-px bg-slate-200">
            {[
              { label: 'الإجمالي',  value: data.summary.total,       color: 'slate' },
              { label: 'مدفوع',     value: data.summary.paid,        color: 'emerald' },
              { label: 'دائن',      value: data.summary.credited,    color: 'amber' },
              { label: 'متبقّي',    value: data.summary.outstanding, color: data.summary.outstanding > 0 ? 'rose' : 'emerald' },
            ].map((s, i) => (
              <div key={i} className={`bg-white p-3 text-center`} data-testid={`lifecycle-summary-${s.label}`}>
                <div className="text-[10px] text-slate-500 uppercase font-semibold">{s.label}</div>
                <div className={`text-base font-bold mt-0.5 ${COLOR_CLASSES[s.color]?.text || 'text-slate-700'}`}>
                  {formatMoney(s.value)}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-5">
          {loading && (
            <div className="text-center py-12 text-slate-500">
              <Loader2 className="w-8 h-8 animate-spin mx-auto mb-2" />
              <p className="text-sm">جاري تحميل الـ Timeline...</p>
            </div>
          )}
          {err && !loading && (
            <div className="bg-rose-50 border border-rose-200 rounded-lg p-4 text-rose-700 text-sm flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 mt-0.5" />
              {err}
            </div>
          )}
          {!loading && data && (
            <div className="relative pl-3">
              {/* Vertical guide line */}
              <div className="absolute right-5 top-2 bottom-2 w-0.5 bg-slate-200" />
              <ol className="space-y-5">
                {(data.events || []).map((ev, idx) => {
                  const vis = EVENT_VISUAL[ev.type] || EVENT_VISUAL.event;
                  const c = COLOR_CLASSES[vis.color];
                  const Icon = vis.icon;
                  return (
                    <li key={idx} className="relative flex items-start gap-3" data-testid={`lifecycle-event-${ev.type}`}>
                      {/* Icon node on the right (RTL) */}
                      <div className={`relative shrink-0 w-10 h-10 rounded-full ${c.bg} text-white flex items-center justify-center shadow-sm ring-4 ${c.ring} ml-auto order-2`}>
                        <Icon className="w-5 h-5" />
                      </div>
                      {/* Event content card */}
                      <div className={`flex-1 ${c.light} border ${c.border} rounded-xl px-4 py-3`}>
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex-1">
                            <div className={`font-semibold ${c.text} text-sm`}>{ev.label}</div>
                            <div className="text-xs text-slate-500 mt-0.5 flex items-center gap-3 flex-wrap">
                              <span>{formatDateTime(ev.timestamp)}</span>
                              {ev.actor && <span>· بواسطة {ev.actor}</span>}
                              {ev.link?.no && (
                                <span className="inline-flex items-center gap-1 bg-white/70 px-1.5 py-0.5 rounded font-mono">
                                  {ev.link.no}
                                  <ExternalLink className="w-3 h-3 opacity-50" />
                                </span>
                              )}
                            </div>
                          </div>
                          {ev.amount != null && (
                            <div className={`text-sm font-bold ${c.text} shrink-0`}>
                              {formatMoney(ev.amount)} ر.س
                            </div>
                          )}
                        </div>
                        {/* Meta details */}
                        {ev.meta && Object.keys(ev.meta).length > 0 && (
                          <div className="mt-2 flex flex-wrap gap-1.5 text-[11px]">
                            {Object.entries(ev.meta).filter(([_, v]) => v != null && v !== '').map(([k, v]) => (
                              <span key={k} className="bg-white/70 text-slate-600 px-1.5 py-0.5 rounded font-mono">
                                {k}: {String(v).slice(0, 40)}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    </li>
                  );
                })}
                {/* Terminal state */}
                {data.events?.length > 0 && (
                  <li className="relative flex items-center gap-3">
                    <div className={`shrink-0 w-10 h-10 rounded-full ${data.is_archived ? 'bg-rose-100 text-rose-600 ring-rose-200' : data.summary?.outstanding <= 0 ? 'bg-emerald-100 text-emerald-600 ring-emerald-200' : 'bg-slate-100 text-slate-400 ring-slate-200'} flex items-center justify-center ring-4 ml-auto order-2`}>
                      <CheckCircle2 className="w-5 h-5" />
                    </div>
                    <div className={`flex-1 text-sm italic ${data.is_archived ? 'text-rose-600' : data.summary?.outstanding <= 0 ? 'text-emerald-600' : 'text-slate-400'}`}>
                      {data.is_archived ? 'الفاتورة مؤرشفة (terminal state)'
                        : data.summary?.outstanding <= 0 ? 'الفاتورة مغلقة كاملاً'
                        : 'في انتظار اكتمال الدفع...'}
                    </div>
                  </li>
                )}
                {(!data.events || data.events.length === 0) && (
                  <li className="text-center py-8 text-slate-400 text-sm">
                    لا توجد أحداث مسجّلة بعد
                  </li>
                )}
              </ol>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-slate-200 px-6 py-3 bg-slate-50 flex items-center justify-between">
          <div className="text-xs text-slate-500">
            {data?.event_count ?? 0} حدث في الجدول الزمني
          </div>
          <button
            onClick={() => onClose?.()}
            className="px-4 py-1.5 text-sm font-medium bg-slate-900 hover:bg-slate-800 text-white rounded-lg"
            data-testid="lifecycle-close-footer"
          >
            إغلاق
          </button>
        </div>
      </div>
    </div>
  );
}
